import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthContext } from 'modules/auth/context';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import { usePortalPlans } from 'modules/portal-plans/usePortalPlans';
import { pickOfferPlan, type PlanOffer } from 'modules/portal-plans/offer';
import { canOfferPlan, offerCooldown, type SubscriptionState } from './eligibility';
import { advance, isBlocking, type Phase, type Steps } from './phases';

export interface SessionEndContext {
  isPortal: boolean;
  isGuest: boolean;
  isPreview: boolean;
}

export interface SessionEndFlow {
  phase: Phase;
  /** True while a step of the sequence is on screen; the result dialog waits for it to clear. */
  blocking: boolean;
  attemptId: string | null;
  offer: PlanOffer | null;
  /** Called once the test has been handed in successfully. Safe to call twice for the same attempt. */
  begin: (attemptId: string) => void;
  /** Moves to the next step; also what closing or skipping a step does. */
  next: () => void;
}

interface Run {
  phase: Phase;
  steps: Steps;
  attemptId: string | null;
}

const IDLE: Run = { phase: 'idle', steps: { offer: false, feedback: false }, attemptId: null };

/**
 * What happens between handing a test in and seeing the result: a short "analysing" screen, a plan
 * offer for learners who do not pay yet, then a request for feedback. Guests and previews get none
 * of it. All the decisions are made here so the test runner only has to call `begin` and `next`.
 */
export function useSessionEndFlow({ isPortal, isGuest, isPreview }: SessionEndContext): SessionEndFlow {
  const { state } = useAuthContext();
  const userId = state.user?.id;
  const subscription = usePortalSubscription();

  const subscriptionState: SubscriptionState = (() => {
    if (subscription.isError || subscription.data === undefined) return 'unknown';
    const data = subscription.data as { periodEnd: string } | null;
    return data && new Date(data.periodEnd).getTime() > Date.now() ? 'active' : 'none';
  })();

  const mayOffer = isPortal && !isGuest && !isPreview && Boolean(userId) && subscriptionState === 'none';
  const plans = usePortalPlans(mayOffer);
  const offer = useMemo(() => (mayOffer ? pickOfferPlan(plans.data) : null), [mayOffer, plans.data]);

  const [run, setRun] = useState<Run>(IDLE);

  // The submit handler is async and may hold the closure of an earlier render; read what is current.
  const latest = useRef({ userId, isPortal, isGuest, isPreview, subscriptionState, offer, organizationTier: state.organization?.tier });
  latest.current = { userId, isPortal, isGuest, isPreview, subscriptionState, offer, organizationTier: state.organization?.tier };
  const startedFor = useRef<string | null>(null);

  const begin = useCallback((attemptId: string) => {
    if (startedFor.current === attemptId) return;
    startedFor.current = attemptId;

    const now = latest.current;
    const signedIn = Boolean(now.userId) && !now.isGuest && !now.isPreview;
    const at = Date.now();
    const offerNow =
      signedIn &&
      canOfferPlan({
        isPortal: now.isPortal,
        isGuest: now.isGuest,
        isPreview: now.isPreview,
        subscription: now.subscriptionState,
        organizationTier: now.organizationTier,
        hasOffer: now.offer !== null,
        lastOfferAt: now.userId ? offerCooldown.read(now.userId) : null,
        now: at,
      });
    if (offerNow && now.userId) offerCooldown.mark(now.userId, at);

    const steps: Steps = { offer: offerNow, feedback: signedIn };
    setRun({ phase: advance('idle', steps), steps, attemptId });
  }, []);

  const next = useCallback(() => {
    setRun((current) => ({ ...current, phase: advance(current.phase, current.steps) }));
  }, []);

  return {
    phase: run.phase,
    blocking: isBlocking(run.phase),
    attemptId: run.attemptId,
    offer: run.steps.offer ? offer : null,
    begin,
    next,
  };
}
